import { OFOSearch, type SearchResult, type SearchDocument, type ContextTerm } from '@rosta/ofo-codes/runtime';

export type { ContextTerm } from '@rosta/ofo-codes/runtime';

const INDEX_URL = '/ofo/index.json';
const EMBED_URL = '/ofo/embeddings.f32';

const DB_NAME = 'bp-ofo-cache';
const STORE = 'kv';

let instancePromise: Promise<SearchBundle> | null = null;
let aiReady = false;
let aiLoading = false;
const aiReadyCallbacks = new Set<() => void>();

interface SearchBundle {
  search: OFOSearch;
  byTitle: Map<string, SearchDocument>;
}

export type OfoSuggestion = SearchResult;

/**
 * Search context — keywords that bias occupation search toward what the user
 * has previously viewed, so a prior "Electrician" selection floats "Electrician"
 * above the closely-related "Electrical Engineer" on a later partial query like
 * "Electr".
 *
 * We keep a *history* of viewed terms with timestamps and derive context weights
 * via exponential decay: recently viewed occupations weigh more than those seen
 * earlier in the session. This is what lets recency drive prioritisation without
 * the caller having to track weights itself — it just records a view.
 *
 * @see ../../../libs/ofo-codes/docs/search-pipeline.md — §"Context awareness"
 *      for how the derived weights feed the offline rerank on the runtime side.
 */
const CONTEXT_HISTORY_KEY = 'bp-ofo-context-history';
/** Drop history older than this (7 days) so stale sessions don't linger. */
const CONTEXT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** Recency half-life: a view this many minutes ago counts as half-weight. */
const CONTEXT_HALF_LIFE_MIN = 10;
/** Never carry more than this many context terms into a query. */
const CONTEXT_CAP = 5;
/** Drop terms decayed below this weight. */
const CONTEXT_MIN_WEIGHT = 0.05;

type ContextHistory = Record<string, number>;

function loadHistory(): ContextHistory {
  try {
    const raw = sessionStorage.getItem(CONTEXT_HISTORY_KEY);
    if (raw) return JSON.parse(raw) as ContextHistory;
  } catch {
    /* ignore */
  }
  return {};
}

function saveHistory(h: ContextHistory): void {
  try {
    sessionStorage.setItem(CONTEXT_HISTORY_KEY, JSON.stringify(h));
  } catch {
    /* storage best-effort */
  }
}

/** Record that the user just viewed `term` (e.g. selected it from the dropdown). */
export function recordContextView(term: string): void {
  const t = term.trim();
  if (!t) return;
  const h = loadHistory();
  h[t] = Date.now();
  const cutoff = Date.now() - CONTEXT_MAX_AGE_MS;
  for (const k of Object.keys(h)) if ((h[k] ?? 0) < cutoff) delete h[k];
  saveHistory(h);
}

/**
 * Derive context terms with exponential-decay weights from the view history.
 * `weight = 0.5 ** (ageMinutes / halfLife)`. Recent views dominate; old ones
 * fall away. Returns the strongest `cap` terms above `minWeight`.
 */
export function buildSearchContext(
  halfLifeMin: number = CONTEXT_HALF_LIFE_MIN,
  cap: number = CONTEXT_CAP,
): ContextTerm[] {
  const h = loadHistory();
  const now = Date.now();
  const terms: ContextTerm[] = [];
  for (const [term, ts] of Object.entries(h)) {
    const ageMin = Math.max(0, (now - (ts ?? 0)) / 60000);
    const weight = Math.pow(0.5, ageMin / halfLifeMin);
    if (weight >= CONTEXT_MIN_WEIGHT) terms.push({ term, weight });
  }
  terms.sort((a, b) => (b.weight ?? 1) - (a.weight ?? 1));
  return terms.slice(0, cap);
}

/**
 * Explicitly override the derived context (e.g. a caller that already computed
 * weighted terms). Pass `null` to clear the override and fall back to the
 * recency-decayed history.
 */

// ── IndexedDB cache (versioned by index createdAt) ──────────────────────────
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbPut(key: string, val: unknown): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* cache best-effort */
  }
}

// ── gzip inflate (DecompressionStream is built into modern browsers) ────────
async function gunzip(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const ds = new DecompressionStream('gzip');
  const stream = new Response(buf).body!.pipeThrough(ds);
  return await new Response(stream).arrayBuffer();
}

async function fetchGzipJson(url: string): Promise<any> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  const buf = await gunzip(await res.arrayBuffer());
  return JSON.parse(new TextDecoder().decode(buf));
}

async function fetchGzipBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  return gunzip(await res.arrayBuffer());
}

// ── Bundle loading (lazy, cached, offline-capable) ──────────────────────────
interface SearchBundle {
  search: OFOSearch;
  byTitle: Map<string, SearchDocument>;
}

async function loadBundle(): Promise<SearchBundle> {
  let indexJson: any = null;
  try {
    indexJson = await fetchGzipJson(INDEX_URL);
  } catch {
    indexJson = await idbGet('index-latest');
  }
  if (!indexJson) throw new Error('OFO index unavailable (offline and not cached)');

  const search = OFOSearch.loadSync(indexJson);
  const byTitle = new Map<string, SearchDocument>();
  for (const doc of search.entries()) byTitle.set(doc.description, doc);

  idbPut('index-latest', indexJson);
  loadEmbeddings(search, indexJson.createdAt).catch(() => {});

  return { search, byTitle };
}

async function loadEmbeddings(search: OFOSearch, version: string): Promise<void> {
  if (aiReady || aiLoading) return;
  aiLoading = true;
  try {
    let embBuf = await idbGet<ArrayBuffer>(`emb-${version}`);
    if (!embBuf) {
      embBuf = await fetchGzipBuffer(EMBED_URL);
      await idbPut(`emb-${version}`, embBuf);
    }
    search.setVectorsFromFloat32(embBuf);
    aiReady = true;
    aiReadyCallbacks.forEach((cb) => cb());
    aiReadyCallbacks.clear();
  } catch {
    aiReady = false;
  } finally {
    aiLoading = false;
  }
}

async function getInstance(): Promise<SearchBundle> {
  if (!instancePromise) instancePromise = loadBundle();
  return instancePromise;
}

// ── Public API ──────────────────────────────────────────────────────────────
export function isAiReady(): boolean {
  return aiReady;
}

/** Run `cb` immediately if AI vectors are loaded, else once they finish loading. */
export function whenAiReady(cb: () => void): void {
  if (aiReady) cb();
  else aiReadyCallbacks.add(cb);
}

// ── Semantic model (transformers.js) — lazy, only for phrase queries ────────
let modelReady = false;
let modelLoading = false;
let modelFailed = false;
const modelCallbacks = new Set<() => void>();

/** Resolve once the embedding model is loaded; reject on timeout / permanent failure. */
function whenModelReadyP(timeoutMs = 25000): Promise<void> {
  if (modelReady) return Promise.resolve();
  if (modelFailed) return Promise.reject(new Error('embedding model unavailable'));
  return new Promise<void>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('embedding model timeout')), timeoutMs);
    whenModelReady(() => {
      clearTimeout(t);
      resolve();
    });
  });
}

export function isModelReady(): boolean {
  return modelReady;
}

/** Run `cb` once the embedding model has loaded (or immediately if already loaded). */
export function whenModelReady(cb: () => void): void {
  if (modelReady) cb();
  else modelCallbacks.add(cb);
}

/**
 * Lazily load the transformers.js model so the first semantic query is fast.
 * Requires vectors (aiReady) because semanticQuery needs them. No-op if already
 * loading/loaded or vectors aren't ready yet.
 */
async function ensureModel(search: OFOSearch): Promise<void> {
  if (modelReady || modelLoading || modelFailed || !aiReady) return;
  modelLoading = true;
  try {
    await search.semanticQuery('warmup', 1);
    modelReady = true;
    modelCallbacks.forEach((cb) => cb());
    modelCallbacks.clear();
  } catch {
    modelLoading = false;
    modelFailed = true;
  }
}

function toSuggestion(
  doc: SearchDocument,
  score = 0,
  rerankScore?: number,
  contextScore?: number,
): OfoSuggestion {
  return {
    code: doc.code,
    description: doc.description,
    majorGroup: doc.majorGroupTitle,
    minorGroup: doc.minorGroupTitle,
    skillLevel: doc.skillLevel,
    score,
    rerankScore: rerankScore ?? score,
    confidence: 1,
    primaryStage: 'lexical',
    stages: ['lexical'],
    vectorScore: 0,
    lexicalScore: 0,
    graphScore: 0,
    fuzzyScore: 0,
    prefixScore: 0,
    tasks: doc.tasks,
    alternateTitles: doc.alternateTitles,
    generatedDescription: doc.generatedDescription,
    contextScore,
  };
}

/**
 * Rich autocomplete. Always returns trie-based prefix/typo matches instantly.
 * Once AI vectors are loaded, results are re-ranked/enriched with the hybrid
 * lexical + graph + vector `query()` so semantically related occupations surface.
 */
export async function searchOfoCodes(
  query: string,
  limit = 8,
  context?: ContextTerm[],
): Promise<OfoSuggestion[]> {
  if (query.trim().length < 2) return [];
  const { search, byTitle } = await getInstance();

  // Search context biases ranking toward the user's prior (recently viewed)
  // occupations. An explicit `context` argument wins; otherwise the recency-
  // decayed view history is used. See `buildSearchContext` / `@rosta/ofo-codes`
  // docs/search-pipeline.md §"Context awareness".
  const ctx = context && context.length ? context : buildSearchContext();

  const titles = search.autocomplete(query, limit);
  const trieResults: OfoSuggestion[] = [];
  for (const t of titles) {
    const doc = byTitle.get(t);
    if (doc) trieResults.push(toSuggestion(doc, 1)); // prefix matches rank first
  }

  // The trie is an instant prefix matcher and is the primary autocomplete
  // signal: typing "elec" should surface "Electrician" immediately, with no
  // dependency on the embedding vectors. Use it directly when vectors are
  // unavailable, and as the top-ranked set when they are.
  if (trieResults.length > 0 && !aiReady) {
    return trieResults.slice(0, limit);
  }
  if (!aiReady) {
    // No prefix match yet and vectors not ready: best-effort lexical fallback.
    return search.lexicalQuery(query, limit);
  }

  const hybrid = search.query(query, { limit: limit + 6, context: ctx });
  const merged = new Map<string, OfoSuggestion>();
  // Hybrid results carry the context-aware `rerankScore` (and `contextScore`),
  // so seed the merge from them first and only fill gaps with trie prefix
  // matches. This way a previously-viewed "Electrician" keeps its context boost
  // instead of being overwritten by a bare trie entry.
  for (const r of hybrid) {
    const doc = search.lookup(r.code);
    if (doc) merged.set(r.code, toSuggestion(doc, r.score, r.rerankScore, r.contextScore));
  }
  for (const s of trieResults) {
    if (!merged.has(s.code)) merged.set(s.code, s); // prefix presence, rerankScore = 1
  }

  // True semantic retrieval for phrase / longer natural-language queries.
  // (Single-token prefixes stay on the instant trie autocomplete.)
  const q = query.trim();
  const isPhrase = q.includes(' ') || q.length >= 5;
  if (isPhrase) {
    ensureModel(search);
    try {
      await whenModelReadyP();
      const sem = await search.semanticQuery(q, limit + 4);
      for (const r of sem) {
        const doc = search.lookup(r.code);
        // Semantic hits lead: score them above the hybrid/trie band.
        if (doc) merged.set(r.code, toSuggestion(doc, r.score, r.score + 2));
      }
    } catch {
      /* model unavailable: keep hybrid/trie results */
    }
  }

  // Rank by `rerankScore` (which folds in the context boost on the runtime
  // side) so a prior "Electrician" view floats it above "Electrical Engineer".
  // Tie-break on `score` for stable, relevance-ordered results.
  return Array.from(merged.values())
    .sort((a, b) => b.rerankScore - a.rerankScore || b.score - a.score)
    .slice(0, limit);
}

/**
 * True semantic search (embeds the query). Requires the embedding model to be
 * available in the browser (transformers.js, lazy-loaded per spec). If the model
 * is unavailable, falls back to the rich hybrid search above.
 */
export async function semanticOfoCodes(query: string, limit = 10): Promise<OfoSuggestion[]> {
  if (query.trim().length < 2) return [];
  const { search } = await getInstance();
  try {
    const results = await search.semanticQuery(query, limit);
    return results.map((r) => ({
      ...r,
      code: r.code,
      description: r.description,
      majorGroup: r.majorGroup,
      minorGroup: r.minorGroup,
      skillLevel: r.skillLevel,
      tasks: r.tasks,
      alternateTitles: r.alternateTitles,
    }));
  } catch {
    return searchOfoCodes(query, limit);
  }
}

export async function autocompleteOfoCodes(query: string, limit = 10): Promise<string[]> {
  if (query.trim().length < 2) return [];
  const { search } = await getInstance();
  return search.autocomplete(query, limit);
}

/** Full occupation detail by exact OFO code (for the explorer detail panel). */
export async function lookupOfoCode(code: string): Promise<OfoSuggestion | null> {
  const { search } = await getInstance();
  const doc = search.lookup(code);
  return doc ? toSuggestion(doc, 1) : null;
}

// ── SLM-generated occupation descriptions (markdown) ─────────────────────────
// Served as a compact code→detail map so the candidate profile can show a rich
// "what is this occupation?" panel without the full search index or vectors.
const DESC_URL = '/ofo/ofo-descriptions.json';

export type OfoDescription = {
  code: string;
  title: string;
  generatedDescription: string;
  majorGroup: string;
  subMajorGroup: string;
  minorGroup: string;
  unitGroup: string;
  skillLevel: string;
  tasks: string[];
  alternateTitles: string[];
};

let descPromise: Promise<Record<string, OfoDescription>> | null = null;

async function loadDescriptions(): Promise<Record<string, OfoDescription>> {
  if (!descPromise) {
    descPromise = (async () => {
      try {
        const res = await fetch(DESC_URL, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`descriptions ${res.status}`);
        return (await res.json()) as Record<string, OfoDescription>;
      } catch {
        return {};
      }
    })();
  }
  return descPromise;
}

/**
 * Normalise an OFO code to its description-map key. Descriptions are keyed by the
 * release-prefixed code (e.g. `2021-671101`) because occupation codes drift between
 * OFO releases; a bare 6-digit code is assumed to belong to the 2021 release.
 */
export function normalizeOfoKey(code: string): string {
  const trimmed = code.trim();
  return /^2021-/i.test(trimmed) ? trimmed : `2021-${trimmed}`;
}

/**
 * Resolve a free-text skill/occupation name to its best-matching OFO code using
 * the in-browser hybrid index, then return the SLM-generated description detail.
 * Used by the candidate profile skill drill-down when no exact code is available.
 */
export async function resolveSkillDescription(name: string): Promise<OfoDescription | null> {
  const trimmed = name.trim();
  if (trimmed.length < 2) return null;
  try {
    const [descMap, match] = await Promise.all([loadDescriptions(), searchOfoCodes(trimmed, 1)]);
    const code = match[0]?.code;
    if (!code) return null;
    return descMap[normalizeOfoKey(code)] ?? null;
  } catch {
    return null;
  }
}

/** Fetch the SLM-generated description detail for an exact (release-prefixed) OFO code. */
export async function getOfoDescription(code: string): Promise<OfoDescription | null> {
  const descMap = await loadDescriptions();
  return descMap[normalizeOfoKey(code)] ?? null;
}
